import React, { useCallback, useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { TextSelection } from 'prosemirror-state';
import StarterKit from '@tiptap/starter-kit';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { createLowlight } from 'lowlight';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { makeStyles } from 'tss-react/mui';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import css from 'highlight.js/lib/languages/css';
import php from 'highlight.js/lib/languages/php';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
const lowlight = createLowlight();
lowlight.register('javascript', javascript);
lowlight.register('typescript', typescript);
lowlight.register('html', xml);
lowlight.register('css', css);
lowlight.register('php', php);
lowlight.register('python', python);
lowlight.register('json', json);
lowlight.register('bash', bash);
const languages = [
    'texte',
    'javascript',
    'typescript',
    'html',
    'css',
    'bash',
    'json',
    'php',
    'python',
    'sql',
    'yaml',
    'dockerfile',
    'nginx',
    'markdown'
];
const useStyles = makeStyles()(() => ({
    editorContainer: {
        flex: 1,
        border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 8,
        background: 'rgba(255,255,255,0.08)',
        overflow: 'hidden',
        minWidth: 0,
        height: 120,
    },
    toolbar: {
        display: 'flex',
        gap: 4,
        padding: '6px 8px',
        borderBottom: '1px solid rgba(255,255,255,0.15)',
        background: 'rgba(255,255,255,0.06)'
    },
    toolButton: {
        width: 28,
        height: 24,
        borderRadius: 4,
        border: '1px solid rgba(255,255,255,0.2)',
        background: 'rgba(255,255,255,0.08)',
        color: '#fff',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
        '&:hover': {
            background: 'rgba(255,255,255,0.15)'
        },
        '&:disabled': {
            opacity: 0.4,
            cursor: 'not-allowed'
        },
        '&.active': {
            background: '#1976d2',
            borderColor: '#1976d2',
            boxShadow: '0 0 8px rgba(25,118,210,0.8)',
            transform: 'scale(1.08)'
        },
        '&.uploading': {
            background: '#ff9800',
            borderColor: '#ff9800',
            animation: 'pulse 1.5s infinite'
        }
    },
    editorContent: {
        padding: '6px 8px',
        height: 82,
        overflow: 'hidden',
        color: '#fff',
        fontSize: 14,
        position: 'relative',
        '& .ProseMirror': {
            outline: 'none',
            height: '44px',
            maxHeight: '44px',
            overflowY: 'auto',
            overflowX: 'hidden',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            paddingRight: 0,
            flex: 1,
        },
        '& .ProseMirror:not(:has(pre))': {
            paddingRight: '48px',
        },
        '& .ProseMirror p': {
            margin: 0
        },
        '& .ProseMirror p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            color: 'rgba(255,255,255,0.6)',
            float: 'left',
            height: 0,
            pointerEvents: 'none'
        },
        '& code': {
            overflowX: 'auto',
            fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
            fontSize: '13px',
            lineHeight: 1.4,
        },
        '& pre code': {
            background: 'none',
            padding: 0,
            borderRadius: 0,
            color: '#f8f8f2',
            fontFamily: 'inherit',
            fontSize: 'inherit',
        },
        '& .hljs-keyword': {
            color: '#c586c0',
        },
        '& .hljs-string': {
            color: '#ce9178',
        },
        '& .hljs-number': {
            color: '#b5cea8',
        },
        '& .hljs-comment': {
            color: '#6a9955',
        },
        '& .hljs-function': {
            color: '#dcdcaa',
        },
        '& .hljs-title': {
            color: '#dcdcaa',
        },
        '& .hljs-variable': {
            color: '#9cdcfe',
        },
        '& .hljs-class': {
            color: '#4ec9b0',
        },
        '& .hljs-tag': {
            color: '#569cd6',
        },
        '& .hljs-attribute': {
            color: '#92c5f8',
        },
        '& .hljs-property': {
            color: '#9cdcfe',
        },
        '& .hljs-built_in': {
            color: '#dcdcaa',
        },
        '& .hljs-operator': {
            color: '#d4d4d4',
        },
        '& .hljs-punctuation': {
            color: '#d4d4d4',
        },
    },
    codeBlockWrapper: {
        maxWidth: 'calc(100% - 48px)',
        height: '66px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    codeBlockHeader: {
        height: 22,
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 8px',
        borderRadius: '6px 6px 0 0',
        background: 'rgba(255,255,255,0.08)',
        flexShrink: 0, // L'entête ne rétrécit pas
    },
    codeLanguageSelect: {
        background: 'transparent',
        color: 'rgba(255,255,255,0.85)',
        border: 'none',
        fontSize: 12,
        outline: 'none',
        cursor: 'pointer',
        maxWidth: 130,
        '& option': {
            color: '#000',
        },
    },
    codeBlockDots: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 16,
        lineHeight: 1,
    },
    sendButton: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: '#1976d2',
        border: 'none',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#1565c0',
            transform: 'scale(1.05)',
        },
        '&:disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
            backgroundColor: '#1976d2',
            transform: 'none',
        },
        '& svg': {
            width: 16,
            height: 16,
            fill: '#fff',
        },
    },
}));
const TiptapEditor = ({ onSend, placeholder, disabled, onFileUpload, isUploading, uploadProgress, accept }) => {
    const [active, setActive] = useState({
        bold: false,
        italic: false,
        underline: false,
        code: false
    });
    const [selectedLanguage, setSelectedLanguage] = useState('texte');
    const { classes } = useStyles();
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                blockquote: false,
                horizontalRule: false,
                codeBlock: false,
                underline: false
            }),
            CodeBlockLowlight.configure({
                lowlight,
                defaultLanguage: 'javascript',
                HTMLAttributes: {
                    class: 'code-block-wrapper'
                }
            }),
            Underline,
            Placeholder.configure({
                placeholder
            })
        ],
        content: '',
        editable: !disabled,
        editorProps: {
            handleKeyDown(view, event) {
                const isCodeBlock = editor?.isActive('codeBlock');
                // Shift+Entrée : saut de ligne
                if (event.key === 'Enter' && event.shiftKey && !event.ctrlKey && !event.metaKey) {
                    if (isCodeBlock) {
                        // Dans un bloc de code : forcer le saut de ligne sans sortir
                        event.preventDefault();
                        editor?.chain().focus().insertContent('\n').run();
                        return true;
                    }
                    else {
                        // Hors d'un bloc de code : comportement normal
                        return false;
                    }
                }
                if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a' && isCodeBlock) {
                    event.preventDefault();
                    const { state, dispatch } = view;
                    const { $from } = state.selection;
                    const codeBlockPos = $from.before();
                    const codeBlockNode = $from.node();
                    const from = codeBlockPos + 1;
                    const to = codeBlockPos + codeBlockNode.nodeSize - 1;
                    dispatch(state.tr.setSelection(TextSelection.create(state.doc, from, to)));
                    return true;
                }
                if (event.key === 'Enter' && event.ctrlKey) {
                    event.preventDefault();
                    const html = editor?.getHTML() || '';
                    const text = editor?.getText() || '';
                    if (text.trim()) {
                        onSend(html);
                        editor?.commands.clearContent();
                    }
                    return true;
                }
                // Entrée simple envoie le message (même dans les blocs de code)
                if (event.key === 'Enter' && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
                    event.preventDefault();
                    const html = editor?.getHTML() || '';
                    const text = editor?.getText() || '';
                    if (text.trim()) {
                        onSend(html);
                        editor?.commands.clearContent();
                    }
                    return true;
                }
                return false;
            }
        },
    });
    const updateActiveButtons = useCallback(() => {
        if (!editor) {
            return;
        }
        setActive({
            bold: editor.isActive('bold'),
            italic: editor.isActive('italic'),
            underline: editor.isActive('underline'),
            code: editor.isActive('codeBlock')
        });
    }, [editor]);
    useEffect(() => {
        editor?.setEditable(!disabled);
    }, [editor, disabled]);
    useEffect(() => {
        if (!editor) {
            return;
        }
        updateActiveButtons();
        editor.on('selectionUpdate', updateActiveButtons);
        editor.on('transaction', updateActiveButtons);
        return () => {
            editor.off('selectionUpdate', updateActiveButtons);
            editor.off('transaction', updateActiveButtons);
        };
    }, [editor, updateActiveButtons]);
    const run = useCallback((action) => {
        if (!disabled) {
            action();
        }
    }, [disabled]);
    return (React.createElement("div", { className: classes.editorContainer },
        React.createElement("div", { className: classes.toolbar },
            React.createElement("button", { type: "button", className: `${classes.toolButton} ${isUploading ? 'uploading' : ''}`, onClick: onFileUpload, disabled: disabled || isUploading, title: "Joindre un fichier" }, isUploading ? (React.createElement("span", { style: { fontSize: '10px', fontWeight: 'bold' } },
                uploadProgress,
                "%")) : ('📎')),
            React.createElement("button", { type: "button", className: `${classes.toolButton} ${active.bold ? 'active' : ''}`, onClick: () => run(() => editor?.chain().focus().toggleBold().run()), disabled: disabled, title: "Gras" }, "B"),
            React.createElement("button", { type: "button", className: `${classes.toolButton} ${active.italic ? 'active' : ''}`, onClick: () => run(() => editor?.chain().focus().toggleItalic().run()), disabled: disabled, title: "Italique" }, "I"),
            React.createElement("button", { type: "button", className: `${classes.toolButton} ${active.underline ? 'active' : ''}`, onClick: () => run(() => editor?.chain().focus().toggleUnderline().run()), disabled: disabled, title: "Soulign\u00E9" }, "U"),
            React.createElement("button", { type: "button", className: `${classes.toolButton} ${active.code ? 'active' : ''}`, onClick: () => run(() => {
                    const isCodeBlock = editor?.isActive('codeBlock');
                    if (isCodeBlock) {
                        editor?.chain().focus().setParagraph().run();
                        setSelectedLanguage('texte');
                    }
                    else {
                        const defaultLanguage = 'javascript';
                        setSelectedLanguage(defaultLanguage);
                        editor?.chain()
                            .focus()
                            .setCodeBlock({ language: defaultLanguage })
                            .run();
                    }
                }), disabled: disabled, title: "Code" }, '</>')),
        React.createElement("div", { className: classes.editorContent },
            editor?.isActive('codeBlock') ? (React.createElement("div", { className: classes.codeBlockWrapper },
                React.createElement("div", { className: classes.codeBlockHeader },
                    React.createElement("select", { className: classes.codeLanguageSelect, value: selectedLanguage, onChange: e => {
                            const newLanguage = e.target.value;
                            setSelectedLanguage(newLanguage);
                            editor?.chain()
                                .focus()
                                .setCodeBlock({ language: newLanguage })
                                .run();
                        } }, languages
                        .filter(lang => lang !== 'texte')
                        .map(lang => (React.createElement("option", { key: lang, value: lang }, lang)))),
                    React.createElement("span", { className: classes.codeBlockDots }, "\u2022\u2022\u2022")),
                React.createElement(EditorContent, { editor: editor }))) : (React.createElement(EditorContent, { editor: editor })),
            React.createElement("button", { type: "button", className: classes.sendButton, onClick: () => {
                    const html = editor?.getHTML() || '';
                    const text = editor?.getText() || '';
                    console.log('TiptapEditor: Envoi du message', {
                        textLength: text.length,
                        hasContent: text.trim().length > 0
                    });
                    if (text.trim() && text.length > 0) {
                        try {
                            console.log('TiptapEditor: Appel de onSend avec HTML:', html.substring(0, 100));
                            onSend(html);
                            setTimeout(() => {
                                console.log('TiptapEditor: Clear content après envoi');
                                editor?.commands.clearContent();
                                editor?.commands.focus();
                            }, 100);
                        }
                        catch (error) {
                            console.error('Erreur lors de l\'envoi:', error);
                        }
                    }
                    else {
                        console.warn('TiptapEditor: Pas de contenu à envoyer');
                    }
                }, disabled: disabled, title: "Envoyer" },
                React.createElement("svg", { viewBox: "0 0 24 24", fill: "currentColor" },
                    React.createElement("path", { d: "M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" }))))));
};
export default TiptapEditor;
